import { useEffect, useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Typography, Divider, TextField, Box, useTheme } from '@mui/material';
import {
  DataGridPro,
  GridActionsCellItem,
  GridColDef,
  GridEventListener,
  GridRowEditStopReasons,
  GridRowId,
  GridRowModel,
  GridRowModes,
  GridRowModesModel,
  GridRowSelectionModel,
  GridToolbarContainer,
  useGridApiRef,
} from '@mui/x-data-grid-pro';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import { FormikProps } from 'formik';
import type {
  Item,
  PackingSlipCreateInput,
  PackingSlipItem,
} from '@repo/api-contract';
import { useDispatch, useSelector } from '../../../../state';
import { useTranslation } from 'react-i18next';
import React from 'react';
import { openSearchItemModal } from '../../../../state/itemSearchModalSlice';
import {
  closeModal,
  openPackingSlipModal,
  setDisableButtons,
} from '../../../../state/packingSlipModalSlice';
import hexToRGBA from '../../../../util/converters/hexToRGBA';
import { getFormikField } from '../../../../util/fornik-helpers';
import { openNewItemModal } from '../../../../state/itemModalSlice';
import BulkActionsMenu from '../../../utils/OrderActionsMenu';
import {
  EMPTY_LEADING_GRID_COLUMNS,
  GRID_CHECKBOX_COLUMN_ORDER,
  usePersistedDataGridColumnPreferences,
} from '../../../../util/hooks';
import type { DataGridTableId } from '../../../../util/dataGridTableIds';
import { GRID_CHECKBOX_SELECTION_FIELD } from '@mui/x-data-grid';
import { DataGridTranslatedColumnHeader } from '../../../dataGrid/DataGridTranslatedColumnHeader';

const TABLE_ID = 'packing-slip-items' satisfies DataGridTableId;
const GLOBAL_COMMON_COL_I18N = 'team.Global.Common';
const GLOBAL_BUTTONS_COL_I18N = 'team.Global.Buttons';
const ADD_PACKING_SLIP_COL_I18N =
  'team.Modals.Packing Slips.createAndEdit.Add Packing Slip';
const columnPadding = '1 2px';

export default function PackingSlipItems({
  formik,
  viewMode,
}: {
  formik: FormikProps<PackingSlipCreateInput>;
  viewMode: boolean;
}): JSX.Element {
  const { t: tPS } = useTranslation([], {
    keyPrefix: ADD_PACKING_SLIP_COL_I18N,
  });

  const theme = useTheme();
  const dispatch = useDispatch();

  const { isOpenPackingSlipModal, formData, disableButtons } = useSelector(
    ({ packingSlipModalSlice }) => packingSlipModalSlice,
  );

  const [items, setItems] = useState<PackingSlipItem[]>([]);
  const [rowModesModel, setRowModesModel] = React.useState<GridRowModesModel>(
    {},
  );
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>(
    [],
  );
  const apiRef = useGridApiRef();

  const genUUID = () => uuidv4();

  const onImport = async (selectedItems: Item[]) => {
    const currentItemIds = new Set(formData.items.map((item) => item.itemId));
    const mappedItems = selectedItems
      .map((item) => ({
        description: item.details.description,
        quantityOrdered: 0,
        quantityShipped: 0,
        unitNumber: item.details.unit,
        itemId: item.ItemId,
        taxable: item.details.taxable,
        vendorItemNumber: item.details.vendorItemNumber,
        customerItemNumber: item.details.customerItemNumber || '',
        vendorCost: item.details.vendorCost ?? 0,
        customerPrice: item.details.customerPrice,
        commissionable: item.details.commissionable,
        commissionRate: item.details.commissionRate,
      }))
      .filter((orderItem) => !currentItemIds.has(orderItem.itemId));

    const initialItems = [...formik.values.items, ...mappedItems];
    formik.setFieldValue('items', initialItems);
    dispatch(openPackingSlipModal());
  };

  const newItemCallBack = async (item: Item | undefined): Promise<void> => {
    dispatch(openPackingSlipModal());
    if (!item) return;

    const newOrderItem = {
      description: item.details.description,
      quantityOrdered: 0,
      quantityShipped: 0,
      unitNumber: item.details.unit,
      itemId: item.ItemId,
      taxable: item.details.taxable,
      vendorItemNumber: item.details.vendorItemNumber,
      customerItemNumber: item.details.customerItemNumber || '',
      vendorCost: item.details.vendorCost ?? 0,
      customerPrice: item.details.customerPrice,
      commissionable: item.details.commissionable,
      commissionRate: item.details.commissionRate,
    };
    formik.setFieldValue('items', [...formik.values.items, newOrderItem]);
  };

  const handleAddRowClick = () => {
    const tempId = `Custom-${genUUID()}`;
    const newRow = {
      itemId: tempId,
      description: '',
      quantityOrdered: 0,
      quantityShipped: 0,
      unitNumber: '',
      taxable: true,
      vendorItemNumber: '',
      customerItemNumber: '',
      vendorCost: 0,
      customerPrice: 0,
      commissionable: false,
      commissionRate: 0.0,
    };
    setItems([...formik.values.items, newRow]);
    formik.setFieldValue('items', [...formik.values.items, newRow]);

    setRowModesModel((prevModel) => ({
      ...prevModel,
      [tempId]: { mode: GridRowModes.Edit },
    }));
  };

  const onCancel = () => {
    dispatch(openPackingSlipModal());
  };

  const handleImportClick = () => {
    dispatch(closeModal());
    dispatch(openSearchItemModal({ onImport, onCancel }));
  };

  const handleCreateClick = () => {
    dispatch(closeModal());
    dispatch(openNewItemModal({ createCallback: newItemCallBack }));
  };

  function EditToolbar(props: {
    selectionModel: GridRowSelectionModel;
    setSelectionModel: (m: GridRowSelectionModel) => void;
    items: any[];
    setItems: React.Dispatch<React.SetStateAction<any[]>>;
    formik: any;
    viewMode: boolean;
    onAddRow: () => void;
  }) {
    const {
      selectionModel,
      setSelectionModel,
      items,
      setItems,
      formik,
      viewMode,
      onAddRow,
    } = props;

    return (
      <GridToolbarContainer sx={{ justifyContent: 'flex-end' }}>
        <BulkActionsMenu
          selectedIds={selectionModel}
          clearSelection={() => setSelectionModel([])}
          items={items}
          setItems={setItems}
          formik={formik}
          viewMode={viewMode}
          onAddRow={onAddRow}
          importItemClick={handleImportClick}
          createItemClick={handleCreateClick}
        />
      </GridToolbarContainer>
    );
  }

  const handleRowEditStop: GridEventListener<'rowEditStop'> = (
    params,
    event,
  ) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const handleEditClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const handleSaveClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };

  const handleDeleteClick = (id: GridRowId) => () => {
    formik.setFieldValue(
      'items',
      items.filter((item) => item.itemId !== id),
    );
    setItems(items.filter((item) => item.itemId !== id));
  };

  const handleCancelClick = (id: GridRowId) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });
  };

  const processRowUpdate = (newRow: GridRowModel) => {
    const originalRow = items.find((item) => item.itemId === newRow.id);

    if (!originalRow) {
      throw new Error('Original row not found.');
    }

    const updatedRow = {
      ...originalRow,
      ...newRow,
      isNew: false,
    };

    const updatedItems = items.map((item) =>
      item.itemId === newRow.id ? updatedRow : item,
    );

    setItems(updatedItems);
    formik.setFieldValue('items', updatedItems);

    return updatedRow;
  };

  const handleRowModesModelChange = (newRowModesModel: GridRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  const numberTypeController = (value: any, row: any) => {
    return value < 0 ? 0 : parseFloat(value);
  };

  const baseColumns: GridColDef[] = useMemo(
    () => [
      {
        field: 'quantityOrdered',
        headerName: 'Qty. Ordered',
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Qty. Ordered"
            params={params}
            keyPrefix={ADD_PACKING_SLIP_COL_I18N}
          />
        ),
        editable: true,
        type: 'number',
        flex: 1,
        align: 'left' as const,
        headerAlign: 'left' as const,
        valueParser: numberTypeController,
      },
      {
        field: 'quantityShipped',
        headerName: 'Qty. Shipped',
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Qty. Shipped"
            params={params}
            keyPrefix={ADD_PACKING_SLIP_COL_I18N}
          />
        ),
        editable: true,
        type: 'number',
        flex: 1,
        align: 'left' as const,
        headerAlign: 'left' as const,
        valueParser: numberTypeController,
      },
      {
        field: 'unitNumber',
        headerName: 'Unit',
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Unit"
            params={params}
            keyPrefix={ADD_PACKING_SLIP_COL_I18N}
          />
        ),
        editable: true,
        flex: 0.5,
      },
      {
        field: 'customerItemNumber',
        headerName: 'Item No.',
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Item No."
            params={params}
            keyPrefix={ADD_PACKING_SLIP_COL_I18N}
          />
        ),
        editable: true,
        flex: 1,
        align: 'left' as const,
        headerAlign: 'left' as const,
      },
      {
        field: 'description',
        headerName: 'Description',
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Description"
            params={params}
            keyPrefix={GLOBAL_COMMON_COL_I18N}
          />
        ),
        editable: true,
        flex: 1,
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Actions',
        renderHeader: (params) => (
          <DataGridTranslatedColumnHeader
            labelKey="Actions"
            params={params}
            keyPrefix={GLOBAL_BUTTONS_COL_I18N}
          />
        ),
        width: 80,
        cellClassName: 'actions',
        getActions: ({ id }) => {
          const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

          if (isInEditMode) {
            return [
              <GridActionsCellItem
                disabled={viewMode}
                icon={<SaveIcon />}
                label="Save"
                sx={{
                  color: 'primary.main',
                }}
                onClick={handleSaveClick(id)}
              />,
              <GridActionsCellItem
                disabled={viewMode}
                icon={<CancelIcon />}
                label="Cancel"
                className="textPrimary"
                onClick={handleCancelClick(id)}
                color="inherit"
              />,
            ];
          }

          return [
            <GridActionsCellItem
              key="edit"
              disabled={viewMode || disableButtons}
              icon={<EditIcon />}
              label="Edit"
              className="textPrimary"
              onClick={handleEditClick(id)}
              color="inherit"
            />,
            <GridActionsCellItem
              key="delete"
              disabled={viewMode || disableButtons}
              icon={<DeleteIcon />}
              label="Delete"
              onClick={handleDeleteClick(id)}
              color="inherit"
            />,
          ];
        },
      },
    ],
    [
      formik,
      rowModesModel,
      viewMode,
      disableButtons,
      handleSaveClick,
      handleCancelClick,
      handleEditClick,
      handleDeleteClick,
      numberTypeController,
    ],
  );

  const persistedPrefs = usePersistedDataGridColumnPreferences({
    tableId: TABLE_ID,
    columns: baseColumns,
    apiRef,
    leadingGridColumnFields: viewMode
      ? EMPTY_LEADING_GRID_COLUMNS
      : GRID_CHECKBOX_COLUMN_ORDER,
  });

  const columns = useMemo(
    () => persistedPrefs.applyPersistedPreferences(baseColumns),
    [baseColumns, persistedPrefs.applyPersistedPreferences],
  );

  useEffect(() => {
    setItems(formik.values.items);
  }, [isOpenPackingSlipModal, formik.values.items]);

  return (
    <>
      <Typography color="primary" component="div">
        {tPS('ITEMS')}
      </Typography>
      <Divider sx={{ marginBottom: 1 }} />
      <Box sx={{ height: 500, width: '100%', overflowX: 'auto' }}>
        <DataGridPro
          apiRef={apiRef}
          columnHeaderHeight={56}
          disableRowSelectionOnClick
          disableColumnMenu={viewMode}
          disableDensitySelector={viewMode}
          disableColumnSelector={viewMode}
          disableMultipleRowSelection={viewMode}
          isCellEditable={() => !viewMode}
          checkboxSelection={!viewMode}
          rowSelectionModel={selectionModel}
          onRowSelectionModelChange={(newSelection) =>
            setSelectionModel(newSelection)
          }
          sx={{
            border: 0,
            '& .MuiDataGrid-cell': {
              padding: columnPadding,
            },
            '& .MuiDataGrid-columnHeader': {
              padding: columnPadding,
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              whiteSpace: 'normal',
              lineHeight: 1.2,
            },
          }}
          onRowEditStop={handleRowEditStop}
          hideFooter
          disableColumnFilter
          rowModesModel={rowModesModel}
          onRowModesModelChange={handleRowModesModelChange}
          processRowUpdate={processRowUpdate}
          slots={{
            toolbar: (props) => (
              <EditToolbar
                {...props}
                selectionModel={selectionModel}
                setSelectionModel={setSelectionModel}
                items={items}
                setItems={setItems}
                formik={formik}
                viewMode={viewMode}
                onAddRow={handleAddRowClick}
              />
            ),
          }}
          rows={items.map((item) => ({
            ...item,
            id: item.itemId,
          }))}
          columns={columns}
          columnVisibilityModel={persistedPrefs.columnVisibilityModel}
          onColumnVisibilityModelChange={
            persistedPrefs.onColumnVisibilityModelChange
          }
          onColumnWidthChange={persistedPrefs.onColumnWidthChange}
          onColumnOrderChange={persistedPrefs.onColumnOrderChange}
          pinnedColumns={{
            right: ['actions'],
            ...(!viewMode ? { left: [GRID_CHECKBOX_SELECTION_FIELD] } : {}),
          }}
          initialState={{
            pagination: { paginationModel: { pageSize: 100 } },
          }}
          pageSizeOptions={[items?.length || 0]}
        />
      </Box>
      <Divider sx={{ marginY: 3 }} />
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <TextField
          disabled={viewMode}
          variant="filled"
          label={tPS('Notes')}
          multiline
          rows={4}
          fullWidth
          sx={{
            backgroundColor: hexToRGBA(theme.palette.primary.main, 0.08),
          }}
          name="notes"
          error={Boolean(getFormikField(formik, 'notes').error)}
          helperText={getFormikField(formik, 'notes').error}
          value={getFormikField(formik, 'notes').value}
          onChange={formik.handleChange}
        />
      </Box>
    </>
  );
}
